import { useEffect, useMemo, useState } from 'react';
import { usePageTranslation, useRoleDisplayName } from '@/shared/hooks';
import logoText from '../../assets/logo_text.png';
import { Menu as MenuIcon, Home as HomeIcon, Logout } from '@/shared/ui/icons';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ChunkErrorBoundary } from './ChunkErrorBoundary';
import { NewVersionBanner } from './NewVersionBanner';
import { RouteStalePanel } from './StaleAppPanel';
import { useAppVersionWatch } from '@/shared/hooks/useAppVersionWatch';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { devMenuSections, menuSections } from './navigationMenu';
import { findActiveItemPath, isHomeActive, type OpenSections } from './navigationState';
import { useNavOpenSections } from './useNavOpenSections';
import { apiClient } from '../utils/api';
import { cx } from '@/shared/ui/utils';

interface LayoutProps {
    children?: React.ReactNode;
}

/** 與 Tailwind 的 lg 斷點一致：以上側邊欄常駐，以下改為蓋在內容上的抽屜 */
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

const ALL_MENU_SECTIONS = [...menuSections, ...devMenuSections];

const RAIL_COLLAPSED_CLASSES = 'is-drawer-close:tooltip is-drawer-close:tooltip-right';

function SidebarLink({
    to,
    label,
    icon,
    isActive,
    onNavigate,
}: {
    to: string;
    label: string;
    icon: React.ReactNode;
    isActive: boolean;
    onNavigate: () => void;
}) {
    return (
        <Link
            to={to}
            className={cx(RAIL_COLLAPSED_CLASSES, isActive ? 'menu-active' : '')}
            aria-current={isActive ? 'page' : undefined}
            data-tip={label}
            onClick={onNavigate}
        >
            <span className="nav-icon">{icon}</span>
            <span className="is-drawer-close:hidden">{label}</span>
        </Link>
    );
}

function Sidebar({
    locationPath,
    openSections,
    activeSectionKey,
    onToggleSection,
    onNavigate,
}: {
    locationPath: string;
    openSections: OpenSections;
    activeSectionKey: string | undefined;
    onToggleSection: (section: string) => void;
    onNavigate: () => void;
}) {
    const { t } = usePageTranslation({ filePath: 'shared/components/Layout' });
    const { hasPermission } = useAuth();
    const { isDark } = useTheme();
    const activeItemPath = findActiveItemPath(ALL_MENU_SECTIONS, locationPath);

    const visibleSections = useMemo(
        () =>
            ALL_MENU_SECTIONS.map((section) => ({
                ...section,
                items: section.items.filter(
                    (item) => !item.permission || hasPermission(item.permission)
                ),
            })).filter((section) => section.items.length > 0),
        [hasPermission]
    );

    return (
        <nav
            aria-label={t('systemTitle')}
            className="app-sidebar flex min-h-full flex-col items-start is-drawer-close:w-14 is-drawer-open:w-64"
        >
            <div className="flex w-full items-center justify-center px-2 py-4 is-drawer-open:px-4">
                <img
                    src={logoText}
                    alt="Teegle"
                    className={cx(
                        'h-10 is-drawer-close:hidden',
                        isDark ? 'brightness-0 invert' : ''
                    )}
                />
                <div className="avatar placeholder is-drawer-open:hidden">
                    <div className="flex items-center justify-center w-10 rounded-full bg-primary text-primary-content">
                        <span className="text-lg font-semibold">T</span>
                    </div>
                </div>
            </div>

            <ul className="menu app-nav w-full grow">
                <li>
                    <SidebarLink
                        to="/"
                        label={t('home')}
                        icon={<HomeIcon />}
                        isActive={isHomeActive(locationPath)}
                        onNavigate={onNavigate}
                    />
                </li>

                {visibleSections.map((section) => (
                    <li key={section.titleKey}>
                        <details open={openSections[section.titleKey]}>
                            <summary
                                className="is-drawer-close:hidden"
                                data-has-active={
                                    section.titleKey === activeSectionKey ? 'true' : undefined
                                }
                                onClick={(event) => {
                                    event.preventDefault();
                                    onToggleSection(section.titleKey);
                                }}
                            >
                                <span>{t(section.titleKey)}</span>
                            </summary>
                            <ul className="ms-3 ps-0 is-drawer-close:ml-0 is-drawer-close:px-0">
                                {section.items.map((item) => (
                                    <li key={item.textKey}>
                                        <SidebarLink
                                            to={item.path}
                                            label={t(item.textKey)}
                                            icon={item.icon}
                                            isActive={item.path === activeItemPath}
                                            onNavigate={onNavigate}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </details>
                    </li>
                ))}
            </ul>

            <div className="w-full px-6 pb-4 pt-3 text-[0.6875rem] leading-4 text-base-content/60 is-drawer-close:hidden">
                <div>
                    {t('frontendVersion')} {__APP_VERSION__}
                </div>
                <div>
                    {new Date(__BUILD_TIME__).toLocaleString('zh-TW', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                    })}
                </div>
                {apiClient.getBackendVersion() ? (
                    <div className="mt-2">
                        <div>
                            {t('backendVersion')} {apiClient.getBackendVersion()}
                        </div>
                        {apiClient.getBackendBuildTime() ? (
                            <div>
                                {new Date(apiClient.getBackendBuildTime()!).toLocaleString(
                                    'zh-TW',
                                    {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false,
                                    }
                                )}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </nav>
    );
}

export default function Layout({ children }: LayoutProps) {
    const { t } = usePageTranslation({ filePath: 'shared/components/Layout' });
    const location = useLocation();
    const { user, logout, isAuthenticated } = useAuth();
    useAppVersionWatch(isAuthenticated);
    const getRoleDisplayName = useRoleDisplayName();
    const [isDesktop, setIsDesktop] = useState(
        () => window.matchMedia(DESKTOP_MEDIA_QUERY).matches
    );
    const [drawerOpen, setDrawerOpen] = useState(isDesktop);

    const { openSections, activeSectionKey, toggleSection } = useNavOpenSections(
        ALL_MENU_SECTIONS,
        location.pathname
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const syncDrawerState = (event?: MediaQueryListEvent) => {
            const matches = event?.matches ?? mediaQuery.matches;
            setIsDesktop(matches);
            setDrawerOpen(matches);
        };

        syncDrawerState();
        mediaQuery.addEventListener('change', syncDrawerState);
        return () => mediaQuery.removeEventListener('change', syncDrawerState);
    }, []);

    const closeMobileMenu = () => {
        if (!window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
            setDrawerOpen(false);
        }
    };

    useEffect(() => {
        if (isDesktop || !drawerOpen) return undefined;
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setDrawerOpen(false);
        };
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [isDesktop, drawerOpen]);

    const drawerId = 'app-layout-drawer';

    return (
        <div className="min-h-screen bg-base-100">
            <div className={cx('drawer', isDesktop && drawerOpen ? 'drawer-open' : '')}>
                <input
                    id={drawerId}
                    type="checkbox"
                    className="drawer-toggle"
                    checked={drawerOpen}
                    onChange={(event) => setDrawerOpen(event.target.checked)}
                />

                <div className="drawer-content min-h-screen bg-base-100">
                    <header className="sticky top-0 z-20">
                        <nav className="navbar bg-base-100 shadow-sm">
                            <div className="navbar-start">
                                <button
                                    type="button"
                                    aria-label={t('openDrawer')}
                                    aria-expanded={drawerOpen}
                                    aria-controls={drawerId}
                                    className="btn btn-square btn-ghost"
                                    onClick={() => setDrawerOpen((current) => !current)}
                                >
                                    <MenuIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="navbar-center">
                                <Link
                                    to="/"
                                    className="btn btn-ghost text-xl normal-case"
                                    onClick={closeMobileMenu}
                                >
                                    {t('systemTitle')}
                                </Link>
                            </div>

                            <div className="navbar-end gap-1 pr-2">
                                <ThemeToggle />
                                <LanguageSwitcher />

                                <div className="dropdown dropdown-end">
                                    <button
                                        type="button"
                                        tabIndex={0}
                                        className="btn btn-ghost gap-2 px-2"
                                    >
                                        <span className="hidden text-sm lg:block">
                                            {user?.name}
                                        </span>
                                        <div className="avatar placeholder">
                                            <div className="flex items-center justify-center w-10 rounded-full bg-primary text-primary-content">
                                                <span>{user?.name?.charAt(0) || 'U'}</span>
                                            </div>
                                        </div>
                                    </button>
                                    <ul
                                        tabIndex={0}
                                        className="menu dropdown-content z-[60] mt-3 w-72 rounded-box bg-base-100 p-2 shadow"
                                    >
                                        <li className="menu-title">
                                            <span>{user?.name}</span>
                                        </li>
                                        <li>
                                            <span>{user?.email}</span>
                                        </li>
                                        <li>
                                            <span>
                                                {t('role')}：
                                                {user ? getRoleDisplayName(user.role) : ''}
                                            </span>
                                        </li>
                                        <li>
                                            <button type="button" onClick={() => logout()}>
                                                <Logout className="h-4 w-4" />
                                                {t('auth.logout')}
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </nav>
                    </header>

                    <NewVersionBanner />

                    <main className="min-h-[calc(100vh-4rem)] bg-base-100 p-4 pb-20 md:p-6 md:pb-20">
                        <ChunkErrorBoundary resetKey={location.key} fallback={<RouteStalePanel />}>
                            {children || <Outlet />}
                        </ChunkErrorBoundary>
                    </main>
                </div>

                <div className="drawer-side is-drawer-close:overflow-visible z-40">
                    <label
                        htmlFor={drawerId}
                        aria-label={t('openDrawer')}
                        className="drawer-overlay"
                    />
                    <Sidebar
                        locationPath={location.pathname}
                        openSections={openSections}
                        activeSectionKey={activeSectionKey}
                        onToggleSection={toggleSection}
                        onNavigate={closeMobileMenu}
                    />
                </div>
            </div>
        </div>
    );
}
